import { createContext, ReactNode, useMemo } from 'react';

import { useResource } from 'react-resource-router';

import { MenuItem, MenuItemGroup } from 'src/components/navigation';
import { accountNavigationResource } from 'src/sections/account/account-navigation-resource';
import { forgeProjectNavigationResource } from 'src/sections/project/forge-project-navigation-resource';
import { projectNavigationResource } from 'src/sections/project/project-navigation-resource';
import { forgeRepositoryNavigationResource } from 'src/sections/repository/forge-repository-navigation-resource';
import { repositoryNavigationResource } from 'src/sections/repository/repository-navigation-resource';
import { forgeWorkspaceNavigationResource } from 'src/sections/workspace/forge-workspace-navigation-resource';
import { workspaceNavigationResource } from 'src/sections/workspace/workspace-navigation-resource';
import { NavigationData } from 'src/types/navigation';

type NavData = {
  menuItems?: NavigationData['menuItems'];
  forgeMenuItems?: MenuItem[];
  forgeAdminMenuItems?: MenuItemGroup[];
  links?: NavigationData['links'];
  user?: NavigationData['user'];
};

const initialValue = {
  user: undefined,
  links: undefined,
  menuItems: undefined,
  forgeMenuItems: undefined,
  forgeAdminMenuItems: undefined,
};

export const NavDataContext = createContext<NavData>(initialValue);

export const NavDataProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { data: accountData } = useResource(accountNavigationResource);
  const { data: workspaceData } = useResource(workspaceNavigationResource);
  const { data: workspaceForgeMenuItems } = useResource(
    forgeWorkspaceNavigationResource
  );
  const { data: projectData } = useResource(projectNavigationResource);
  const { data: projectForgeMenuItems } = useResource(
    forgeProjectNavigationResource
  );
  const { data: repoData } = useResource(repositoryNavigationResource);
  const { data: repoForgeMenuItems } = useResource(
    forgeRepositoryNavigationResource
  );

  const currentResourceNavData =
    repoData || projectData || workspaceData || accountData;

  const { links, menuItems, user } = currentResourceNavData || initialValue;
  // Prefer the most specific scope available for forge menu items based on currentResourceNavData
  // this is so the sidebar nav shows the correct menu items for the current resource
  // as projectForgeMenuItems and workspaceForgeMenuItems can both be available at the same time
  const { forgeMenuItems, forgeAdminMenuItems } =
    repoForgeMenuItems ||
    projectForgeMenuItems ||
    workspaceForgeMenuItems ||
    {};

  const navDataValue = useMemo(() => {
    return {
      user,
      links,
      menuItems,
      forgeMenuItems,
      forgeAdminMenuItems,
    };
  }, [menuItems, user, links, forgeMenuItems, forgeAdminMenuItems]);

  return (
    <NavDataContext.Provider value={navDataValue}>
      {children}
    </NavDataContext.Provider>
  );
};
